import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import { categoryMeta, hotVideos, videos, type VideoCategory } from "@/mocks/videos";
import { useSEO } from "@/hooks/useSEO";

type FilterKey = "all" | VideoCategory;

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [filter, setFilter] = useState<FilterKey>("all");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useSEO({
    title: `${query || "搜索"}_搜索结果 - 粉色视频`,
    description: `粉色视频为你提供「${query || "关键词"}」的搜索结果，快速定位电影、电视剧、综艺等视频内容，便捷查找想看的作品。`,
    keywords: `${query},${query}搜索结果,粉色视频搜索`,
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const matched = videos.filter((v) => {
      const haystack = [
        v.title,
        v.region,
        v.director,
        ...v.genre,
        ...v.cast,
        v.description,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    return filter === "all" ? matched : matched.filter((v) => v.category === filter);
  }, [query, filter]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setSearchParams({ q });
  };

  return (
    <div className="w-full">
      <div className="bg-primary-50">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">搜索</span>
          </nav>
          <h1 className="mt-3 text-2xl font-black text-foreground-950 md:text-3xl">视频搜索</h1>

          <form onSubmit={submit} className="mt-5 flex max-w-2xl items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如：科幻、都市、美食"
                className="h-11 w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        {query ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    filter === f.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <p className="mt-5 text-sm text-foreground-600">
              关键词「<span className="font-semibold text-foreground-900">{query}</span>」共找到{" "}
              <span className="font-bold text-primary-600">{results.length}</span> 个结果
              {filter !== "all" && `（筛选：${categoryMeta[filter].name}）`}
            </p>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
                {results.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="mt-8 rounded-lg bg-background-100 p-8 text-center md:p-12">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <p className="mt-4 text-base font-semibold text-foreground-800">
                  抱歉，没有找到与「{query}」匹配的视频
                </p>
                <p className="mt-2 text-sm text-foreground-500">
                  试试更换关键词，或看看下面的热门推荐
                </p>
                <div className="mt-8">
                  <div className="mb-4 flex items-center justify-center gap-2">
                    <i className="ri-fire-fill text-primary-500"></i>
                    <h2 className="text-base font-bold text-foreground-900">热门推荐</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-left sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
                    {hotVideos.slice(0, 6).map((video) => (
                      <VideoCard key={video.id} video={video} showFavorite={false} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg bg-background-100 p-8 text-center md:p-14">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-800">输入关键词开始搜索</p>
            <p className="mt-2 text-sm text-foreground-500">支持按标题、类型、主演、地区等查找视频</p>
            <div className="mt-8">
              <div className="mb-4 flex items-center justify-center gap-2">
                <i className="ri-fire-fill text-primary-500"></i>
                <h2 className="text-base font-bold text-foreground-900">大家都在看</h2>
              </div>
              <div className="grid grid-cols-2 gap-3 text-left sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
                {hotVideos.slice(0, 6).map((video) => (
                  <VideoCard key={video.id} video={video} showFavorite={false} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}